import {
  Group,
  loadSVGFromURL,
  util,
  classRegistry,
  type FabricObject,
} from 'fabric';
import type { SerializedDrawingObject } from '@/types/drawing';

// SVG-sourced groups (items / shirts / goals) are serialized datalessly as `objects: "<sourcePath>"` —
// a compact URL that reloads the artwork via loadSVGFromURL — rather than embedding the child geometry.
// The built-in `Group` does not handle that string form on either side:
//   - `Group.fromObject` runs `objects.map(...)`, which throws "t.map is not a function" on a URL string.
//   - `Group.toObject` always re-serializes the children, so a dataless re-export would embed the full
//     geometry (measured 1.5–4× larger `drawing_JSON`) and break the compact `drawing_JSON` contract the
//     backend/frontend rely on.
// This subclass restores both halves: `fromObject` reloads from the URL when `objects` is a string, and
// `toDatalessObject` re-emits `objects: sourcePath` (recursing children through their own
// `toDatalessObject` when the group itself has no sourcePath). Array-`objects` groups fall through to the
// base implementation. Registered as the 'group' class so importJSON's `loadFromJSON` uses it.
/* eslint-disable unicorn/no-this-outside-of-class -- typed-`this` helper shared (via .call) between SourcePathGroup.toDatalessObject and the reloaded plain-Group instance patched in fromObject */
function datalessWithSourcePath(
  this: Group,
  propertiesToInclude?: string[],
): Record<string, unknown> {
  const object = (
    Group.prototype.toObject as (p?: string[]) => Record<string, unknown>
  ).call(this, propertiesToInclude);
  const sourcePath = (this as Group & { sourcePath?: string }).sourcePath;
  object.objects =
    sourcePath ||
    this.getObjects()
      .filter(
        (child) =>
          !(child as FabricObject & { excludeFromExport?: boolean })
            .excludeFromExport,
      )
      .map((child) =>
        // Cast to a minimal shape (NOT intersected with FabricObject, whose own
        // `toDatalessObject` return type would widen the call back to `any`).
        (
          child as { toDatalessObject: (p?: string[]) => unknown }
        ).toDatalessObject(propertiesToInclude),
      );
  return object;
}
/* eslint-enable unicorn/no-this-outside-of-class */

class SourcePathGroup extends Group {
  // Signature mirrors Fabric's generic static `fromObject` (object typed loosely to stay assignable to
  // the base static side; the serialized shape is dynamic JSON anyway).
  // `object: unknown` (not `any`) still satisfies the base generic static
  // `fromObject<T extends TOptions<SerializedGroupProps>>` — an override param is checked
  // contravariantly, and `unknown` is a supertype of every `T`. Narrow it ONCE to the known
  // serialized wire shape so the body is type-safe.
  static async fromObject(object: unknown, options?: unknown): Promise<Group> {
    const serialized = object as SerializedDrawingObject;
    if (typeof serialized.objects === 'string') {
      const url = serialized.objects;
      const { objects, options: svgOptions } = await loadSVGFromURL(url);
      const svg = util.groupSVGElements(
        objects.filter((o): o is FabricObject => o !== null),
        svgOptions,
      );
      const opts = { ...serialized };
      delete opts.objects;
      delete opts.type; // `type` is a read-only getter, so it can't be re-applied via `set`
      delete opts.version;
      // A serialized `layoutManager` is a PLAIN object; `svg.set` would overwrite the reloaded group's
      // real LayoutManager INSTANCE with it, so the next `toObject` throws
      // "this.layoutManager.toObject is not a function". Keep the instance groupSVGElements built.
      // (Legacy drawings have no layoutManager → the delete is a harmless no-op for them.)
      delete opts.layoutManager;
      svg.set(opts);
      (svg as Group & { sourcePath?: string }).sourcePath = url; // preserve so a re-export stays dataless
      // The reloaded group is a PLAIN Group (groupSVGElements bypasses classRegistry), so patch the
      // instance to re-emit its sourcePath datalessly (so a re-export stays compact + reloadable).
      (
        svg as Group & { toDatalessObject: typeof datalessWithSourcePath }
      ).toDatalessObject = datalessWithSourcePath;
      return svg as Group;
    }
    return (
      super.fromObject as (o: unknown, opt?: unknown) => Promise<Group>
    ).call(this, serialized, options);
  }

  // The OUTER item/shirt group (a SourcePathGroup via classRegistry, with no sourcePath of its own):
  // recurse children through toDatalessObject so the nested reloaded group's `objects: sourcePath` survives.
  // Narrows the base's `any` return to the real serialized shape (a covariant override return is allowed).
  toDatalessObject(propertiesToInclude?: string[]): Record<string, unknown> {
    return datalessWithSourcePath.call(this, propertiesToInclude);
  }
}

// Register under BOTH type casings. Legacy saved drawings serialize `type:"group"` (lowercase); current
// exports serialize `type:"Group"` (capitalized). Without the 'Group' entry a compact re-export (inner
// `objects:"<sourcePath>"`, type "Group") would resolve to the built-in Group, whose `fromObject` runs
// `objects.map()` on the string → "t.map is not a function". Registering both routes every group (either
// casing, array- or string-`objects`) through SourcePathGroup.
/* eslint-disable unicorn/no-top-level-side-effects -- registering the group-class override (both casings) is the purpose of this module */
classRegistry.setClass(SourcePathGroup, 'group');
classRegistry.setClass(SourcePathGroup, 'Group');
/* eslint-enable unicorn/no-top-level-side-effects */

export default SourcePathGroup;
